<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  releaseChatSurface,
  retainChatSurface,
} from '@/modules/chat-matrix-fast/utils/chatSurface'
import { useChatMessageInput } from '@/modules/chat-matrix-fast/composables/useChatMessageInput'
import { useChatModals } from '@/modules/chat-matrix-fast/composables/useChatModals'
import { useMatrixTyping } from '@/modules/chat-matrix-fast/composables/useMatrixTyping'
import {
  buildNotificationsMenuItem,
  useActiveChatNotifications,
} from '@/modules/chat-matrix-fast/composables/useActiveChatNotifications'
import { buildPinChatMenuItem, canPinChatInList } from '@/modules/chat-matrix-fast/composables/useChatPinMenu'
import {
  getCurrentMatrixUserId,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { useChatComposerReady } from '@/modules/chat-matrix-fast/composables/useChatComposerReady'
import { useChatStructurePoll } from '@/modules/chat-matrix-fast/composables/useChatStructurePoll'
import { useMentionMembers } from '@/modules/chat-matrix-fast/composables/useMentionMembers'
import { useChatFileDrop } from '@/modules/chat-matrix-fast/composables/useChatFileDrop'
import { useChatLogStickToBottom } from '@/modules/chat-matrix-fast/composables/useChatLogStickToBottom'
import { scrollToChatMessage } from '@/modules/chat-matrix-fast/utils/scrollToChatMessage'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useChatStore, useChatStoreCore } from '@/views/apps/chat-matrix-fast/chatModules'
import ChatHeader from '@/views/apps/chat-matrix-fast/components/ChatHeader.vue'
import ChatInput from '@/views/apps/chat-matrix-fast/components/ChatInput.vue'
import ChatLog from '@/views/apps/chat-matrix-fast/components/ChatLog.vue'
import MessageSelectionBar from '@/views/apps/chat-matrix-fast/components/chat-log/MessageSelectionBar.vue'
import ChatPanelSkeleton from '@/views/apps/chat-matrix-fast/components/ChatPanelSkeleton.vue'
import FileUploadDialog from '@/views/apps/chat-matrix-fast/components/dialogs/FileUploadDialog.vue'
import EntityAccessDeniedOverlay from '@/components/createdUi/EntityAccessDeniedOverlay.vue'

const props = defineProps<{
  chatId: number | null
  connectError?: string | null
}>()

const emit = defineEmits<{
  (e: 'hideChat'): void
}>()

const store = useChatStore()
const chatStoreCore = useChatStoreCore()
const modals = useChatModals()

onMounted(() => retainChatSurface())
onUnmounted(() => releaseChatSurface())

const chatLogPS = ref<HTMLElement | null>(null)
interface ChatLogHandle {
  clearMessageSelection: () => void
  openForwardSelected: () => void
}

const chatLogRef = ref<ChatLogHandle | null>(null)
const isMessageSelecting = ref(false)
const selectedMessageCount = ref(0)
const { beginOpenStick, onTimelineSignal, stickNow } = useChatLogStickToBottom(chatLogPS)

function onMessageSelectionChange(payload: { active: boolean; count: number }) {
  isMessageSelecting.value = payload.active
  selectedMessageCount.value = payload.count
}

const scrollToBottomInChatLog = () => {
  stickNow()
}

const handleQuoteClick = (
  messageId: number | string,
  matrixEventId?: string,
) => {
  scrollToChatMessage(chatLogPS.value, messageId, matrixEventId, {
    ensureEvent: async eventId => {
      await chatStoreCore.loadMessagesAroundEvent(eventId)
      store.syncWithCoreStore()
    },
    refetch: async () => {
      const chatId = store.activeChat?.chat?.id
      if (!chatId) {
        return
      }

      await chatStoreCore.fetchMessages(chatId, { mergeWithExisting: true })
      store.syncWithCoreStore()
    },
  }).then(found => {
    if (!found) {
      useFeedbackStore().showToast({
        type: 'info',
        message: 'Не удалось перейти к исходному сообщению',
        time: 3000,
      })
    }
  })
}

const openEventUrl = () => {
  const url = store.activeChat?.chat && 'event_url' in store.activeChat.chat
    ? store.activeChat.chat.event_url
    : null

  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}

const activeMatrixRoomId = computed(() => {
  const activeChatId = store.activeChat?.chat?.id
  if (!activeChatId) {
    return null
  }

  const currentChat = chatStoreCore.currentChat?.id === activeChatId
    ? chatStoreCore.currentChat
    : chatStoreCore.chats.find(chat => chat.id === activeChatId)

  return currentChat?.matrix_room_id ?? null
})

const activeCoreChat = computed(() => {
  const activeChatId = props.chatId ?? store.activeChat?.chat?.id
  if (!activeChatId) {
    return null
  }
  if (chatStoreCore.currentChat?.id === activeChatId) {
    return chatStoreCore.currentChat
  }
  return chatStoreCore.chats.find(chat => chat.id === activeChatId) ?? null
})

const {
  canCompose,
  showComposer,
  readyStatus,
  showTimeline,
  readyMessage,
  retryConnection,
  retrying,
  canRetryConnection,
} = useChatComposerReady({
  chat: activeCoreChat,
})

const isChatProvisioning = computed(() => Boolean(props.chatId) && !canCompose.value)
const isActiveChatMatched = computed(() =>
  props.chatId != null && store.activeChat?.chat?.id === props.chatId,
)

const {
  failed: structurePollFailed,
  failureMessage: structurePollFailureMessage,
  retry: retryStructurePoll,
} = useChatStructurePoll({
  chatId: computed(() => props.chatId),
  blocked: isChatProvisioning,
  readyStatus,
  canCompose,
  onRoomReady: async chatId => {
    await store.getChat(chatId)
  },
})

const showChatSkeleton = computed(() => {
  if (props.connectError || structurePollFailed.value || readyStatus.value === 'failed') {
    return false
  }

  if (!props.chatId || !isActiveChatMatched.value) {
    return true
  }

  return isChatProvisioning.value && !showTimeline.value
})

const mentionChatId = computed(() => props.chatId ?? store.activeChat?.chat?.id)
const mentionRoomId = computed(() => activeMatrixRoomId.value ?? undefined)
const {
  members: mentionMembers,
  ensureMentionMembers,
} = useMentionMembers({
  chatId: mentionChatId,
  matrixRoomId: mentionRoomId,
})

const excludeMentionKey = computed(() => getCurrentMatrixUserId())

const {
  typingText,
  markTyping,
  stopTyping,
} = useMatrixTyping(activeMatrixRoomId)

const {
  isChatNotificationsEnabled,
  notificationButtonIcon,
} = useActiveChatNotifications(() => store.activeChat?.chat?.id)

const embeddedChatMoreList = computed(() => {
  const list: Array<{
    title: string
    value: string
    prependIcon?: string | (() => string)
    onClick?: () => void
    type?: string
    model?: typeof isChatNotificationsEnabled
  }> = [
    buildNotificationsMenuItem(isChatNotificationsEnabled, notificationButtonIcon),
  ]

  const chat = store.activeChat?.chat
  const coreChat = chat?.id != null
    ? (chatStoreCore.currentChat?.id === chat.id
        ? chatStoreCore.currentChat
        : chatStoreCore.chats.find(item => item.id === chat.id))
    : null
  const matrixRoomId = coreChat?.matrix_room_id
    ?? ('matrix_room_id' in (chat ?? {}) ? (chat as { matrix_room_id?: string }).matrix_room_id : undefined)

  if (chat?.id && canPinChatInList(chat.id, matrixRoomId)) {
    list.unshift(buildPinChatMenuItem({
      isPinned: Boolean(chat.is_pinned ?? coreChat?.is_pinned),
      onClick: () => {
        chatStoreCore.setChatPinned(chat.id, !(chat.is_pinned ?? coreChat?.is_pinned))
      },
    }))
  }

  return list
})

/**
 * Поведение ввода и отправки — из общего композбла. SlidingChat
 * использует тот же — правки чат-разработчика прилетают сюда автоматически.
 */
const {
  msg,
  editingMessage,
  replyTo,
  sendMessage,
  cancelEdit,
  cancelReply,
  handleReply,
  handleStartEdit,
  onMessageKeydown,
} = useChatMessageInput({
  onAfterSend: () => scrollToBottomInChatLog(),
})

const submitMessage = async (mentionUserIds?: string[]) => {
  stopTyping()
  await sendMessage(mentionUserIds)
}

const onPasteFiles = (files: File[]) => {
  if (!files.length) return
  modals.openFileUploadDialog(files)
}

const {
  isDraggingFiles,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
} = useChatFileDrop({
  enabled: () => showComposer.value,
  onFiles: files => modals.openFileUploadDialog(files),
})

const onConsumedInitialFiles = () => {
  modals.pendingUploadFiles.value = []
}

const onMessageSent = () => {
  cancelReply()
  nextTick(() => scrollToBottomInChatLog())
}

watch(
  msg,
  value => {
    if (value.trim()) {
      markTyping()
    } else {
      stopTyping()
    }
  },
)

let loadGeneration = 0
watch(
  () => props.chatId,
  async id => {
    const generation = ++loadGeneration
    if (!id) return
    if (!chatStoreCore.isInitialized) await chatStoreCore.initializeOnce()
    if (generation !== loadGeneration) return
    await store.getChat(id)
    if (generation !== loadGeneration) return
    nextTick(() => {
      beginOpenStick()
    })
  },
  { immediate: true },
)

watch(
  () => {
    const messages = chatStoreCore.messages
    const last = messages.length > 0 ? messages[messages.length - 1] : null
    return {
      chatId: chatStoreCore.currentChat?.id ?? null,
      count: messages.length,
      lastKey: last
        ? String(last.matrix_event_id || last.id)
        : null,
    }
  },
  (next, prev) => {
    nextTick(() => {
      onTimelineSignal(next, prev)
    })
  },
)
</script>

<template>
  <div
    class="embedded-chat d-flex flex-column h-100"
    :class="{ 'chat-panel--drag-over': isDraggingFiles }"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div
      v-if="isDraggingFiles"
      class="chat-panel-drop-hint"
    >
      Отпустите файл сюда
    </div>
    <template v-if="showChatSkeleton">
      <ChatPanelSkeleton
        show-hide-button
        @hide-chat="emit('hideChat')"
      />
    </template>

    <template v-else-if="!props.chatId">
      <div class="embedded-chat-header-row d-flex align-center position-relative" style="z-index: 6;">
        <div class="flex-grow-1" />
        <VBtn
          icon="ri-play-fill"
          variant="text"
          color="primary"
          class="ms-2"
          aria-label="Скрыть чат"
          @click="emit('hideChat')"
        />
      </div>
      <VDivider />
      <div class="position-relative flex-grow-1 overflow-hidden">
        <EntityAccessDeniedOverlay
          v-if="props.connectError"
          variant="chat"
        />
        <div
          v-else
          class="d-flex align-center justify-center flex-grow-1 text-medium-emphasis text-body-2 px-4 text-center h-100"
        />
      </div>
    </template>

    <template v-else>
      <!-- Шапка с названием, счётчиком участников и кнопкой «Скрыть чат» -->
      <div class="embedded-chat-header-row d-flex align-center">
        <ChatHeader
          class="flex-grow-1 overflow-hidden"
          :active-chat="store.activeChat"
          :show-back-button="false"
          show-hide-button
          show-member-count
          :show-type-prefix="false"
          :typing-text="typingText"
          @hide-chat="emit('hideChat')"
          @open-event="openEventUrl"
        />
        <MoreBtn
          class="me-2 flex-shrink-0"
          :menu-list="embeddedChatMoreList"
        />
      </div>

      <VDivider />

      <!-- Лог сообщений — тот же ChatLog что в SlidingChat -->
      <div ref="chatLogPS" class="flex-grow-1 chat-messages-scroll">
        <div
          v-if="structurePollFailed || readyStatus === 'failed'"
          class="d-flex align-center justify-center h-100 text-medium-emphasis text-body-2 px-4 text-center"
        >
          <div class="d-flex flex-column align-center ga-4">
            <div>
              {{ readyMessage || structurePollFailureMessage || 'Не удалось подключиться' }}
            </div>
            <VBtn
              v-if="canRetryConnection"
              color="primary"
              variant="outlined"
              :loading="retrying"
              @click="readyStatus === 'failed' ? retryConnection() : retryStructurePoll()"
            >
              Повторить
            </VBtn>
          </div>
        </div>
        <ChatLog
          v-else
          ref="chatLogRef"
          @reply="handleReply"
          @start-edit="handleStartEdit"
          @quote-click="handleQuoteClick"
          @selection-change="onMessageSelectionChange"
        />
      </div>

      <MessageSelectionBar
        v-if="isMessageSelecting"
        :count="selectedMessageCount"
        @cancel="chatLogRef?.clearMessageSelection()"
        @forward="chatLogRef?.openForwardSelected()"
      />

      <ChatInput
        v-else-if="showComposer && isActiveChatMatched"
        v-model="msg"
        :editing-message="editingMessage"
        :reply-to="replyTo"
        :members="mentionMembers"
        :exclude-mention-key="excludeMentionKey"
        :ensure-members="ensureMentionMembers"
        @send="submitMessage"
        @cancel-edit="cancelEdit"
        @cancel-reply="cancelReply"
        @paste-files="onPasteFiles"
        @keydown="onMessageKeydown"
      />
    </template>

    <FileUploadDialog
      v-model="modals.isFileUploadDialogOpen.value"
      :initial-files="modals.pendingUploadFiles.value"
      :reply-to="replyTo"
      @message-sent="onMessageSent"
      @consumed-initial-files="onConsumedInitialFiles"
    />
  </div>
</template>

<style lang="scss" scoped>
.embedded-chat {
  position: relative;
  background-color: rgb(var(--v-theme-background));
  overflow: hidden;
}

.chat-panel--drag-over {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

.chat-panel-drop-hint {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  font-size: 0.9375rem;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-surface), 0.88);
}

.embedded-chat-header-row,
.chat-messages-scroll,
:deep(.active-chat-header),
:deep(.chat-log-message-form) {
  background-color: rgb(var(--v-theme-background));
}

.chat-messages-scroll {
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: auto;
}
</style>
